import { ChangeEvent, FC, KeyboardEvent, useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { TranslationPack } from '../../../../i18n';
import { useDirection, useI18n, useUID } from '../../../../hooks';
import StyledInput from '../../../Input/Input.styles';
import { StyledSelect } from '../../../Select/Select';
import { ForwardProps } from '../../../../types';
import { msToTimeUnit, timeUnitToMs } from '../utils';
import StyledDateTime from '../DateTime.styles';
import { StyledFormControl } from '../../../FormControl';
import Flex from '../../../Flex';
import Input from '../../../Input';
import Select, { Option } from '../../../Select';
import { defaultThemeProp } from '../../../../theme';

import DurationInputProps, { Format } from './DurationInput.types';

export const StyledNumberUnit = styled(StyledDateTime)<DurationInputProps>(() => {
  const { start, end } = useDirection();
  return css`
    padding: 0;
    border: 0;
    height: auto;
    &:focus-within:not([readonly]):not([disabled]) {
      box-shadow: none;
    }
    > ${StyledInput} {
      background-color: transparent;
      border-top-${end}-radius: 0;
      border-bottom-${end}-radius: 0;
    }
    > ${StyledSelect} {
      max-width: max-content;
      margin-inline-start: -0.0625rem;
      background-color: transparent;
      border-top-${start}-radius: 0;
      border-bottom-${start}-radius: 0;
    }
    &[readonly] > ${StyledSelect} {
      color: currentcolor;
      opacity: 1;
      cursor: default;
    }
    > ${StyledInput}:hover:not([readonly]):not([disabled]) + ${StyledSelect} {
      border-inline-start: transparent;
    }
  `;
});

StyledNumberUnit.defaultProps = defaultThemeProp;

type NumberUnitProps = DurationInputProps & ForwardProps;

const OPTIONS = [Format.Seconds, Format.Minutes, Format.Hours, Format.Days];

const NumberUnit: FC<NumberUnitProps> = (props: DurationInputProps) => {
  const {
    defaultValue = 0,
    value,
    onChange,
    format = Format.Seconds,
    onSelect,
    disabled,
    readOnly,
    required,
    status,
    autoFocus,
    ...restProps
  } = props;
  const t = useI18n();
  const [valueInMs, setValueInMs] = useState<number>(value ?? defaultValue);
  const [unit, setUnit] = useState<Format>(format);

  useEffect(() => {
    setUnit(format);
  }, [format]);

  useEffect(() => {
    if (value !== undefined) {
      setValueInMs(value);
    }
  }, [value]);

  const onNumberInputChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      const inputValue = Number(ev.target.value);
      if (Number.isNaN(inputValue) || inputValue < 0) return;
      setValueInMs(timeUnitToMs(inputValue, unit));
    },
    [unit]
  );

  const onInputBlur = useCallback(() => {
    if (!disabled && !readOnly && onChange) {
      onChange(valueInMs);
    }
  }, [valueInMs, onChange]);

  const onInputKeyDown = useCallback((ev: KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-', ',', '.'].includes(ev.key)) ev.preventDefault();
  }, []);

  const onSelectChange = useCallback(
    (ev: ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = ev.target.value as Format;
      setUnit(selectedValue);
      onSelect?.(selectedValue);
    },
    [onSelect]
  );

  const unitOptions = OPTIONS.map(option => (
    <Option key={option} value={option}>
      {t(`${option}_text` as keyof TranslationPack)}
    </Option>
  ));

  const id = useUID();

  return (
    <Flex
      as={StyledNumberUnit}
      forwardedAs={StyledFormControl}
      container={{ alignItems: 'center', wrap: 'nowrap' }}
      status={status}
      readOnly={readOnly}
      disabled={disabled}
      {...restProps}
    >
      <Input
        type='number'
        key='number'
        placeholder='0'
        aria-labelledby={id}
        value={msToTimeUnit(valueInMs, unit) as any}
        onChange={onNumberInputChange}
        onBlur={onInputBlur}
        onKeyDown={onInputKeyDown}
        required={required}
        status={status}
        readOnly={readOnly}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      <Select
        id={id}
        aria-label={t(`${unit}_text` as keyof TranslationPack)}
        key='unit'
        value={unit}
        required={required}
        status={status}
        readOnly={readOnly}
        disabled={disabled || readOnly}
        onChange={onSelectChange}
      >
        {unitOptions}
      </Select>
    </Flex>
  );
};

export default NumberUnit;
